"use client";

import * as React from "react";

import { toast } from "sonner";
import { z } from "zod";
import { uploadFile } from "../bpi/plateUpload";
import { useUploadConfig } from "../bpi/uploadContext";

interface UseUploadFileProps {
  onUploadComplete?: (file: UploadedFile) => void;
  onUploadError?: (error: unknown) => void;
  headers?: Record<string, string>;
  onUploadBegin?: (fileName: string) => void;
  onUploadProgress?: (progress: { progress: number }) => void;
  skipPolling?: boolean;
}

interface UploadedFile {
  key: string; // Unique identifier
  url: string; // Public URL of the uploaded file
  name: string; // Original filename
  size: number; // File size in bytes
  type: string; // MIME type
}

export function useUploadFile({ onUploadComplete, onUploadError, ...props }: UseUploadFileProps = {}) {
  const { uploadUrl, downloadUrl, uploadEnabled } = useUploadConfig();
  const [uploadedFile, setUploadedFile] = React.useState<UploadedFile>();
  const [uploadingFile, setUploadingFile] = React.useState<File>();
  const [progress, setProgress] = React.useState<number>(0);
  const [isUploading, setIsUploading] = React.useState(false);

  async function uploadThing(file: File) {
    if (!uploadEnabled) {
      const message = "Image upload not allowed";
      toast.error(message);
      onUploadError?.(message);
      setIsUploading(false);
      setUploadedFile(undefined);
      return { isEnabled: false };
    }

    setIsUploading(true);
    setUploadingFile(file);

    try {
      const uploaded = await uploadFile(file, uploadUrl, downloadUrl);
      //@ts-ignore
      setUploadedFile(uploaded);
      //@ts-ignore
      onUploadComplete?.(uploaded);
      return uploaded;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      const message = errorMessage.length > 0 ? errorMessage : "Something went wrong, please try again later.";

      toast.error(message);

      onUploadError?.(error);

      return null;
    } finally {
      setProgress(0);
      setIsUploading(false);
      setUploadingFile(undefined);
    }
  }

  return {
    isUploading,
    progress,
    uploadedFile,
    uploadFile: uploadThing,
    uploadingFile,
    isEnabled: true,
  };
}

export function getErrorMessage(err: unknown) {
  const unknownError = "Something went wrong, please try again later.";

  if (err instanceof z.ZodError) {
    const errors = err.issues.map((issue) => issue.message);

    return errors.join("\n");
  }
  if (err instanceof Error) {
    return err.message;
  }
  return unknownError;
}

export function showErrorToast(err: unknown) {
  const errorMessage = getErrorMessage(err);

  return toast.error(errorMessage);
}
